/**
 * Turning confirmed document figures into profile facts — and refusing to
 * silently pick a winner when documents disagree.
 *
 * Rules:
 *  - A rough figure typed during intake is replaced by a confirmed document
 *    figure. The intake figure was always meant as a placeholder.
 *  - When two confirmed document figures for the same field and the same (or
 *    an unknown) period differ by more than 2%, that is a conflict. The profile
 *    keeps whatever it had until the owner chooses, and every screen shows the
 *    conflict.
 *  - Figures for different stated years are not in conflict; the most recent
 *    year is used and the others remain visible on the document.
 */

import type {
  Candidate, DocumentRecord, Evidence, FinancialField, Workspace,
} from '../workspace/schema.js';
import { FINANCIAL_FIELD_LABELS, fact, nowIso } from '../workspace/schema.js';

export const CONFLICT_TOLERANCE = 0.02;

export interface SourceFigure {
  candidateId: string;
  docId: string;
  docName: string;
  value: number;
  period?: string;
  page?: number;
  sheet?: string;
  snippet: string;
}

export interface Conflict {
  field: FinancialField;
  label: string;
  period?: string;
  figures: SourceFigure[];
  /** (max - min) / max, as a fraction. */
  spread: number;
  resolved: boolean;
  chosenCandidateId?: string;
}

function confirmedFigures(ws: Workspace, field: FinancialField): SourceFigure[] {
  const out: SourceFigure[] = [];
  for (const d of ws.documents) {
    for (const c of d.candidates) {
      if (c.field !== field || c.state !== 'confirmed') continue;
      out.push({
        candidateId: c.id,
        docId: d.id,
        docName: d.name,
        value: c.value,
        ...(c.period ? { period: c.period } : {}),
        ...(c.page !== undefined ? { page: c.page } : {}),
        ...(c.sheet ? { sheet: c.sheet } : {}),
        snippet: c.snippet,
      });
    }
  }
  return out;
}

function spreadOf(values: number[]): number {
  const max = Math.max(...values.map(Math.abs));
  const min = Math.min(...values.map(Math.abs));
  if (max === 0) return 0;
  // Opposite signs (a profit and a loss) always disagree.
  if (values.some((v) => v < 0) && values.some((v) => v > 0)) return 1;
  return (max - min) / max;
}

/** Most recent stated period wins; figures with no period sit alongside it. */
function latestPeriodGroup(figs: SourceFigure[]): { period?: string; figs: SourceFigure[] } {
  const periods = [...new Set(figs.map((f) => f.period).filter((p): p is string => !!p))]
    .sort((a, b) => (a === 'YTD' ? 1 : b === 'YTD' ? -1 : Number(a) - Number(b)));
  const latest = periods.at(-1);
  return {
    ...(latest ? { period: latest } : {}),
    figs: figs.filter((f) => !f.period || f.period === latest),
  };
}

export function findConflicts(ws: Workspace): Conflict[] {
  const out: Conflict[] = [];
  for (const field of Object.keys(FINANCIAL_FIELD_LABELS) as FinancialField[]) {
    const { period, figs } = latestPeriodGroup(confirmedFigures(ws, field));
    if (figs.length < 2) continue;
    const spread = spreadOf(figs.map((f) => f.value));
    if (spread <= CONFLICT_TOLERANCE) continue;
    const res = ws.resolutions[field];
    const chosen = res && figs.some((f) => f.candidateId === res.chosen) ? res.chosen : undefined;
    out.push({
      field,
      label: FINANCIAL_FIELD_LABELS[field],
      ...(period ? { period } : {}),
      figures: figs,
      spread,
      resolved: !!chosen,
      ...(chosen ? { chosenCandidateId: chosen } : {}),
    });
  }
  return out;
}

export function evidenceFor(d: DocumentRecord, c: Candidate): Evidence {
  return {
    docId: d.id,
    docName: d.name,
    ...(c.page !== undefined ? { page: c.page } : {}),
    ...(c.sheet ? { sheet: c.sheet } : {}),
    snippet: c.snippet,
    ...(c.period ? { period: c.period } : {}),
  };
}

function findCandidate(ws: Workspace, candidateId: string): { doc: DocumentRecord; cand: Candidate } | null {
  for (const doc of ws.documents) {
    const cand = doc.candidates.find((c) => c.id === candidateId);
    if (cand) return { doc, cand };
  }
  return null;
}

/**
 * Recompute every financial profile field from confirmed document figures.
 * Fields with no confirmed document figure keep what the owner entered.
 */
export function syncFinancialsFromDocuments(ws: Workspace): Workspace {
  const profile = { ...ws.profile };
  for (const field of Object.keys(FINANCIAL_FIELD_LABELS) as FinancialField[]) {
    const { figs } = latestPeriodGroup(confirmedFigures(ws, field));
    if (figs.length === 0) {
      // No document supports it any more: drop a figure that came from a
      // document, keep one the owner typed.
      if (profile[field]?.evidence) delete profile[field];
      continue;
    }
    let pick: SourceFigure | undefined;
    if (figs.length === 1 || spreadOf(figs.map((f) => f.value)) <= CONFLICT_TOLERANCE) {
      pick = figs[0];
    } else {
      const res = ws.resolutions[field];
      pick = figs.find((f) => f.candidateId === res?.chosen);
      // Unresolved conflict: leave the profile as it was.
      if (!pick) continue;
    }
    const found = pick ? findCandidate(ws, pick.candidateId) : null;
    if (found) profile[field] = { ...fact(found.cand.value, 'confirmed', evidenceFor(found.doc, found.cand)) };
  }
  return { ...ws, profile };
}

export function setCandidateState(
  ws: Workspace, docId: string, candidateId: string, state: Candidate['state'], value?: number,
): Workspace {
  const documents = ws.documents.map((d) => (d.id !== docId ? d : {
    ...d,
    candidates: d.candidates.map((c) => {
      if (c.id === candidateId) return { ...c, state, ...(value !== undefined ? { value } : {}) };
      // Confirming one figure for a field on a document settles that field for
      // the document: other pending figures for it (other years, other
      // matches) are set aside, so a multi-year statement does not stay
      // "to confirm" forever. They remain visible and can be restored.
      const target = d.candidates.find((x) => x.id === candidateId);
      if (state === 'confirmed' && target && c.state === 'pending' && c.field === target.field) {
        return { ...c, state: 'rejected' as const };
      }
      return c;
    }),
  }));
  return syncFinancialsFromDocuments({ ...ws, documents });
}

export function resolveConflict(ws: Workspace, field: FinancialField, candidateId: string): Workspace {
  return syncFinancialsFromDocuments({
    ...ws,
    resolutions: { ...ws.resolutions, [field]: { chosen: candidateId, at: nowIso() } },
  });
}

/** The owner vouches for a figure they typed themselves. */
export function confirmEnteredFigure(ws: Workspace, field: FinancialField): Workspace {
  const f = ws.profile[field];
  if (!f || f.evidence) return ws;
  return { ...ws, profile: { ...ws.profile, [field]: { ...f, source: 'confirmed', updatedAt: nowIso() } } };
}
